export type PatrolPolicyPresetId =
  | 'bypass'
  | 'relaxed'
  | 'standard'
  | 'strict'
  | 'off';

export type PatrolChannelAccessMode = 'open' | 'linked_only' | 'age_bracket';

export function ageBracketRoleName(minAge: number, maxAge: number): string {
  return `Age-${minAge}-${maxAge}`;
}

export type PatrolGuildRegistryStatus =
  | 'active'
  | 'pending_link'
  | 'suspended';

export interface PatrolDiscordBotHealth {
  ready: boolean;
  loggedInAs: string;
  startupError?: string;
  inviteUrl: string;
  lastCheckedAt: string;
}

export interface PatrolDiscordGuild {
  registryId: string;
  communityId: string | null;
  displayName: string;
  status: PatrolGuildRegistryStatus;
  memberCount: number;
  linkedMemberCount: number;
  linkedAt?: string | null;
  inviteUrl?: string | null;
  iconUrl?: string;
}

export interface PatrolGuildSettings {
  defaultPolicyId: PatrolPolicyPresetId;
  linkRequired: boolean;
  reputationEnabled: boolean;
  ageRoleSyncEnabled: boolean;
  verifiedRoleName: string;
  ageBrackets: Array<{ minAge: number; maxAge: number; roleName: string }>;
  failClosedOnAiOutage: boolean;
  notificationsEmail: boolean;
  kindSpeechEnabled: boolean;
  kindSpeechMilestoneCount: number;
  positiveParticipationHourlyLimit: number;
  kindSpeechPenaltyCooldownMs: number;
  kindSpeechDedupeWindowMs: number;
}

export interface PatrolRolePolicy {
  id: string;
  discordRoleId: string;
  roleName: string;
  roleColor: string;
  policyId: PatrolPolicyPresetId;
  memberCount: number;
}

export interface PatrolChannelPolicy {
  id: string;
  discordChannelId: string;
  channelName: string;
  channelType: 'text' | 'voice' | 'announcement';
  policyId: PatrolPolicyPresetId;
}

export interface PatrolChannelAccessPolicy {
  id: string;
  discordChannelId: string;
  channelName: string;
  channelType: 'text' | 'voice' | 'announcement';
  accessMode: PatrolChannelAccessMode;
  minAge?: number;
  maxAge?: number;
  roleName?: string;
}

export interface PatrolActivityStat {
  id: string;
  labelKey: string;
  value: string;
  trendKey?: string;
  trendParams?: Record<string, string | number>;
  trendDirection?: 'up' | 'down' | 'neutral';
}

export interface PatrolModerationCase {
  caseId: string;
  occurredAt: string;
  channelName: string;
  action: string;
  reason: string;
  reputationDelta?: number;
}

export interface PatrolModQueueItem {
  id: string;
  caseId: string;
  communityId: string;
  platformUserId: string;
  proposedAction: 'kick' | 'ban';
  tier: number;
  status: 'pending' | 'approved' | 'rejected' | 'applied';
  violationType: string;
  severity: string;
  confidence: number;
  strikeCount: number;
  reputationAtProposal: number | null;
  createdAt: string;
}

export interface PatrolCaseEvent {
  occurredAt: string;
  action: string;
  reason: string | null;
  violationType: string | null;
  severity: string | null;
  confidence: number | null;
  reputationDelta: number | null;
  previousReputation: number | null;
  newReputation: number | null;
  channelId: string | null;
  eventType: string | null;
}

export interface PatrolCaseModQueueSnapshot {
  id: string;
  proposedAction: string;
  tier: number;
  status: string;
  strikeCount: number;
  reputationAtProposal: number | null;
  createdAt: string;
  resolvedAt: string | null;
}

export interface PatrolCaseRecord {
  caseId: string;
  communityId: string;
  platformUserId: string | null;
  valvurUserId: string | null;
  linkedOnValvur: boolean;
  channelId: string | null;
  channelName: string | null;
  violationType: string | null;
  severity: string | null;
  confidence: number | null;
  reputationDelta: number | null;
  previousReputation: number | null;
  newReputation: number | null;
  startedAt: string;
  events: PatrolCaseEvent[];
  harmAsOffenderCount: number;
  modQueue: PatrolCaseModQueueSnapshot | null;
  enforcement: { strikeCount: number; lastTier: number } | null;
  exportSummary: string;
}

export interface PatrolCaseSummary {
  caseId: string;
  occurredAt: string;
  platformUserId: string | null;
  channelName: string | null;
  violationType: string | null;
  severity: string | null;
  reputationDelta: number | null;
  primaryAction: string | null;
}

export interface PatrolModerationLedgerEntry {
  caseId: string;
  occurredAt: string;
  platformUserId: string | null;
  channelId: string | null;
  channelName: string | null;
  violationType: string | null;
  severity: string | null;
  reputationDelta: number | null;
  primaryAction: string;
  reason: string | null;
}

export interface PatrolModerationLedgerResponse {
  items: PatrolModerationLedgerEntry[];
  nextBefore: string | null;
}

export interface PatrolDiscordRoleOption {
  discordRoleId: string;
  roleName: string;
  roleColor: string;
  memberCount: number;
}

export interface PatrolDiscordChannelOption {
  discordChannelId: string;
  channelName: string;
  channelType: 'text' | 'voice' | 'announcement';
}

export type PatrolSetupChecklistItemId =
  | 'botInstalled'
  | 'guildLinked'
  | 'defaultPolicy'
  | 'rolePolicies'
  | 'channelPolicies'
  | 'botSync';

export interface PatrolSetupChecklistItem {
  id: PatrolSetupChecklistItemId;
  done: boolean;
  labelKey: string;
}

export interface PatrolDiscordCockpitState {
  botHealth: PatrolDiscordBotHealth;
  guilds: PatrolDiscordGuild[];
  guildSettings: Record<string, PatrolGuildSettings>;
  rolePolicies: Record<string, PatrolRolePolicy[]>;
  channelPolicies: Record<string, PatrolChannelPolicy[]>;
  channelAccessPolicies: Record<string, PatrolChannelAccessPolicy[]>;
  discordRoles: Record<string, PatrolDiscordRoleOption[]>;
  discordChannels: Record<string, PatrolDiscordChannelOption[]>;
  activityStats: Record<string, PatrolActivityStat[]>;
  recentCases: Record<string, PatrolModerationCase[]>;
  modQueueItems: Record<string, PatrolModQueueItem[]>;
}
